'use client';

import { FC, ForwardRefExoticComponent, Fragment, RefAttributes, useMemo } from 'react';
import Link from 'next/link';
import { BarChartBigIcon, ChevronDown, CircleQuestionMark, ExternalLink, Home, LucideProps, Medal, MessageSquare, Trophy, Users } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubItem, useSidebar } from './sidebar';
import { SidebarItem } from './types';
import { deepReplacePlaceholders } from '../../../utils/format';
type NavMenuItemProps = {
  item: SidebarItem;
  params?: Record<string, string>;
};
const iconMap: Record<string, ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>> = {
  home: Home,
  users: Users,
  trophy: Trophy,
  barChartBigIcon: BarChartBigIcon,
  medal: Medal,
  circleQuestionMark: CircleQuestionMark,
  messageSquare: MessageSquare
};
function isPathActive(currentPath: string, itemPath: string): boolean {
  return Boolean(currentPath !== '/' && itemPath && currentPath.startsWith(itemPath));
}
const PRESERVE_PARAMS_LIST = ['season', 'seasonSection', 'tournamentLevel', 'benchmark'];
const PATHS_PARAM_PRESERVE = ['/analytics'];
export const NavMenuItem: FC<NavMenuItemProps> = ({
  item,
  params = {}
}) => {
  const Comp = item.subMenu?.length ? CollapsibleMenuItem : SimpleMenuItem;
  const menuItem = deepReplacePlaceholders(item, params);
  return <Comp item={menuItem} data-sentry-element="Comp" data-sentry-component="NavMenuItem" data-sentry-source-file="nav-menu-item.tsx" />;
};
const SimpleMenuItem: FC<NavMenuItemProps> = ({
  item
}) => {
  const Icon = item?.icon ? iconMap[item?.icon] : undefined;
  const pathname = usePathname() ?? '/';
  const isActive = isPathActive(pathname, item.path);
  const {
    setOpenMobile
  } = useSidebar();
  return <SidebarMenuItem key={item.title} data-sentry-element="SidebarMenuItem" data-sentry-component="SimpleMenuItem" data-sentry-source-file="nav-menu-item.tsx">
      {item.isAction ? <SidebarMenuButton id={item.customId} isActive={isActive} onClick={() => setOpenMobile(false)}>
          {Icon && <Icon size={16} />}
          <span>{item.title}</span>
        </SidebarMenuButton> : <SidebarMenuButton asChild isActive={isActive} onClick={() => setOpenMobile(false)}>
          <Link href={item.path} target={item.isExternal ? '_blank' : ''} className="justify-between">
            <span className="flex gap-2 items-center">
              {Icon && <Icon size={16} />}
              <span className="font-medium">{item.title}</span>
            </span>

            {item.isExternal && <ExternalLink />}
          </Link>
        </SidebarMenuButton>}
    </SidebarMenuItem>;
};
const CollapsibleMenuItem: FC<NavMenuItemProps> = ({
  item
}) => {
  const Icon = item?.icon ? iconMap[item?.icon] : undefined;
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const {
    setOpenMobile
  } = useSidebar();
  const {
    push
  } = useRouter();
  const {
    isParentActive,
    paramQuery
  } = useMemo(() => {
    const isParentActive = isPathActive(pathname, item.path);
    const filteredParams = new URLSearchParams();
    const shouldPreserveParams = PATHS_PARAM_PRESERVE.some(path => pathname.startsWith(path));
    if (shouldPreserveParams) {
      for (const key of PRESERVE_PARAMS_LIST) {
        const value = searchParams.get(key);
        if (value !== null) {
          filteredParams.set(key, value);
        }
      }
    }
    const paramQuery = filteredParams.toString();
    return {
      isParentActive,
      paramQuery
    };
  }, [pathname, item.path, searchParams]);
  return <Collapsible onOpenChange={isOpen => {
    if (isOpen && !isParentActive) {
      const firstItem = item.subMenu?.[0]?.items?.[0];
      if (firstItem) {
        push(firstItem.path);
      }
    } else {
      push('/');
    }
  }} open={isParentActive} defaultOpen={false} className="group/collapsible" data-sentry-element="Collapsible" data-sentry-component="CollapsibleMenuItem" data-sentry-source-file="nav-menu-item.tsx">
      <SidebarMenu data-sentry-element="SidebarMenu" data-sentry-source-file="nav-menu-item.tsx">
        <SidebarMenuItem data-sentry-element="SidebarMenuItem" data-sentry-source-file="nav-menu-item.tsx">
          <CollapsibleTrigger asChild data-sentry-element="CollapsibleTrigger" data-sentry-source-file="nav-menu-item.tsx">
            <SidebarMenuButton asChild isActive={isParentActive} data-sentry-element="SidebarMenuButton" data-sentry-source-file="nav-menu-item.tsx">
              <div className="cursor-pointer">
                {Icon && <Icon size={16} />}
                <span className="font-medium">{item.title}</span>
                <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" data-sentry-element="ChevronDown" data-sentry-source-file="nav-menu-item.tsx" />
              </div>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent data-sentry-element="CollapsibleContent" data-sentry-source-file="nav-menu-item.tsx">
            {item.subMenu?.map((subMenu, s) => <Fragment key={s}>
                {subMenu.title && <SidebarGroupLabel className="uppercase">{subMenu.title}</SidebarGroupLabel>}

                <SidebarMenuSub className="pb-2">
                  {subMenu.items.map((item, i) => <SidebarMenuSubItem key={`${item.title}${i}`}>
                      <SidebarMenuButton asChild isActive={isPathActive(pathname, item.path)} onClick={() => setOpenMobile(false)}>
                        <Link href={`${item.path}${paramQuery ? `?${paramQuery}` : ''}`}>
                          <span className="font-medium">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuSubItem>)}
                </SidebarMenuSub>
              </Fragment>)}
          </CollapsibleContent>
        </SidebarMenuItem>
      </SidebarMenu>
    </Collapsible>;
};